import type { Ref } from 'react';
import clsx from 'clsx';
import Tooltip from './Tooltip';
import type { Placement } from '@floating-ui/react';
import Icon, { IconProps } from './Icon';

interface ButtonIconProps {
  icon: IconProps['name'];
  tooltip?: string;
  title?: string;
  /**
   * Keyboard shortcut to surface inside the tooltip as key cap(s), e.g. 'l' or
   * ['⌘', 'K']. Providing this renders the rich Tooltip (instead of the native
   * `title` tooltip) so the shortcut badge can be shown. Only set this on
   * buttons where pressing the key actually performs the action.
   */
  shortcut?: string | string[];
  tooltipPlacement?: Placement;
  onClick?: (e?: React.MouseEvent) => void;
  size?: IconProps['size'];
  className?: string;
  iconClassName?: string;
  disabled?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  // a11y attrs that popover-style triggers need on the actual <button>.
  // Popover.tsx routes these here via cloneElement so screen readers can
  // announce "menu, collapsed" / "menu, expanded" on the trigger itself
  // instead of an inert wrapper element.
  'aria-expanded'?: boolean | 'true' | 'false';
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  'aria-controls'?: string;
  'aria-pressed'?: boolean | 'true' | 'false' | 'mixed';
  id?: string;
}

function ButtonIcon({
  icon,
  tooltip,
  title,
  shortcut,
  tooltipPlacement,
  onClick,
  size,
  className,
  iconClassName,
  disabled,
  buttonRef,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
  'aria-controls': ariaControls,
  'aria-pressed': ariaPressed,
  id,
}: ButtonIconProps) {
  const label = title ?? tooltip;
  const keys = shortcut == null ? [] : Array.isArray(shortcut) ? shortcut : [shortcut];

  // Render the rich Tooltip whenever there is tooltip content or a shortcut to
  // show; otherwise fall back to the lightweight native title tooltip.
  const useRichTooltip = tooltip != null || keys.length > 0;

  const iconEl = <Icon name={icon} size={size} className={iconClassName} />;

  return (
    <button
      className={clsx('button-icon', className)}
      onClick={(e) => onClick?.(e)}
      ref={buttonRef}
      disabled={disabled}
      aria-label={label}
      // Suppress the native title when the rich Tooltip renders, otherwise both
      // would show on hover.
      title={useRichTooltip ? undefined : label}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      aria-controls={ariaControls}
      aria-pressed={ariaPressed}
      id={id}
    >
      {useRichTooltip ? (
        <Tooltip
          content={<ButtonIconTooltipContent label={label} keys={keys} />}
          placement={tooltipPlacement}
        >
          {iconEl}
        </Tooltip>
      ) : (
        iconEl
      )}
    </button>
  );
}

interface ButtonIconTooltipContentProps {
  label?: string;
  keys: string[];
}

// Tooltip body: the action label followed by its shortcut rendered as small key
// caps styled for the dark tooltip surface (cf. Kbd, which targets the light
// keyboard-shortcuts modal). Exported so non-ButtonIcon triggers (e.g. the
// search field's icon button) can render a matching shortcut badge.
export function ButtonIconTooltipContent({ label, keys }: ButtonIconTooltipContentProps) {
  if (keys.length === 0) return <>{label}</>;

  return (
    <span className="flex items-center gap-2">
      <span>{label}</span>
      <span className="flex items-center gap-0.5">
        {keys.map((key, i) => (
          <kbd
            key={i}
            className={clsx(
              'text-xxs inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-white/15 px-1.5 font-mono leading-none text-white/90',
              // Uppercase single-char letter keys (a, l, K) but leave word-like
              // keys (esc, Ctrl) as authored so they don't read as shouting.
              key.length === 1 && 'uppercase',
            )}
          >
            {key}
          </kbd>
        ))}
      </span>
    </span>
  );
}

export default ButtonIcon;
